// src/modules/telegram/components/TelegramPreview/PreviewFab.tsx
import { FC, useState } from "react"
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material"
import VisibilityIcon from "@mui/icons-material/Visibility"
import CloseIcon from "@mui/icons-material/Close"
import { TgMailing } from "../../interfaces/mailing"
import { TelegramPreview, TelegramPreviewProps } from "./TelegramPreview"

// Methods for which the preview renders a TelegramBubble.
const PREVIEWABLE: TgMailing.AvailableMethods[] = [
  "sendMessage",
  "sendPhoto",
  "sendVideo",
  "sendAnimation",
]

// Methods for which the preview renders a Telegram embed iframe.
// Button is always visible but disabled until fields are filled.
const MTPROTO_PREVIEW_METHODS: TgMailing.AvailableMethods[] = [
  "forwardMessages",
  "copyMessages",
]

/**
 * Returns true when the body has a valid @username in from_chat_id
 * and at least one numeric ID in message_ids (raw string form values).
 *
 * Note: intentionally does not import from TelegramForwardPreview to avoid
 * a cross-module dependency for a simple guard that only needs to check
 * enablement — not build the full URL.
 */
function isMtprotoPreviewEnabled(body: TelegramPreviewProps["body"]): boolean {
  const b = body as Record<string, unknown>
  const fromChatId = String(b?.from_chat_id ?? "").trim()
  const hasUsername = fromChatId.startsWith("@") && fromChatId.length > 1
  const messageIds = String(b?.message_ids ?? "")
  const hasIds = messageIds.split(/[\s,;]+/).some((s) => /^\d+$/.test(s.trim()))
  return hasUsername && hasIds
}

interface PreviewFabProps
  extends Pick<TelegramPreviewProps, "method" | "body" | "botName"> {
  /**
   * Optional async validation to run before opening the preview dialog.
   * Return `true` to allow opening, `false` to block it (and show field errors).
   */
  beforeOpen?: () => Promise<boolean>
  /**
   * Render a compact icon-only button (no label). Useful inside dense layouts
   * such as data-grid cells.
   */
  iconOnly?: boolean
}

/**
 * Inline button that opens a Dialog with a live TelegramPreview.
 *
 * - For sendMessage / sendPhoto / sendVideo / sendAnimation: always active.
 * - For forwardMessages / copyMessages: visible but disabled until
 *   from_chat_id is a @username and message_ids contains a valid number.
 * - For all other methods (e.g. sendChatAction): hidden.
 */
export const PreviewFab: FC<PreviewFabProps> = ({
  method,
  body,
  botName,
  beforeOpen,
  iconOnly,
}) => {
  const [open, setOpen] = useState(false)

  const isMtprotoPreview = !!method && MTPROTO_PREVIEW_METHODS.includes(method)

  if (!method || (!PREVIEWABLE.includes(method) && !isMtprotoPreview))
    return null

  const disabled = isMtprotoPreview && !isMtprotoPreviewEnabled(body)

  // TelegramBubble preview: dark telegram background + white text
  // Iframe preview: no background override — the embed widget has its own background
  const dialogBackground = isMtprotoPreview ? undefined : "#0e1621"
  const dialogTextColor = isMtprotoPreview ? undefined : "#fff"

  const handleClick = async () => {
    if (beforeOpen) {
      const valid = await beforeOpen()
      if (!valid) return
    }
    setOpen(true)
  }

  return (
    <>
      {iconOnly ? (
        <IconButton onClick={handleClick} disabled={disabled} color="primary">
          <VisibilityIcon />
        </IconButton>
      ) : (
        <Button
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={handleClick}
          disabled={disabled}
        >
          Предпросмотр
        </Button>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { background: dialogBackground } }}
      >
        <DialogTitle
          sx={{
            color: dialogTextColor,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 0,
          }}
        >
          Предпросмотр
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ color: dialogTextColor }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TelegramPreview method={method} body={body} botName={botName} />
        </DialogContent>
      </Dialog>
    </>
  )
}
